/**
 * Formatters - Utility functions for formatting display values
 * Following patterns from main B18 app (loadFieldFormatters.ts, SelectAsync.tsx)
 */

/**
 * Parse an amount that may be saved as display text ('$1,500.00'), like the
 * web app's addAndFormatCurrency. Numbers pass through as they are.
 */
const parseAmount = (value: string | number): number =>
  typeof value === 'string'
    ? parseFloat(value.replace(/[$,]/g, ''))
    : Number(value)

/**
 * Format currency value
 * @param value - Number or string value
 * @returns Formatted currency string "$1,234.56" or "-" if empty
 */
export const formatCurrency = (value?: string | number | null): string => {
  try {
    if (value === null || value === undefined || value === '') return '-'
    const num = parseAmount(value)
    if (isNaN(num)) return '-'
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num)
  } catch {
    return '-'
  }
}

/**
 * Format phone number to (XXX) XXX-XXXX format.
 * Port of the web's formatPhoneNumber (components/common/functions.tsx), so
 * both apps print the same text: 10 digits, or 11 with a leading 1, become
 * (XXX) XXX-XXXX; any other length falls back to the web's digit grouping
 * ('5551234' -> '555-123-4'); text with no digits prints nothing.
 */
export const formatPhoneNumber = (phone?: string | null): string => {
  if (!phone) return ''
  const digits = String(phone).trim().replace(/\D/g, '')
  if (!digits) return ''
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  return digits.replace(/(\d{1,3})(\d{1,3})(\d{1,4})/, '$1-$2-$3')
}

/**
 * Format customer/broker name like SelectAsync getSelectAsyncLabel
 * Pattern: "BrokerName (DBA: dbaName) - (MC#: mcNumber)"
 */
export const formatCustomerName = (
  customer?: {
    customerDetails?: {
      brokerName?: string
      dbaName?: string
      mcNumber?: string
    }
  } | null
): string => {
  if (!customer?.customerDetails) return '-'
  const { brokerName, dbaName, mcNumber } = customer.customerDetails
  if (!brokerName) return '-'

  let result = brokerName
  if (dbaName) {
    result += ` (DBA: ${dbaName})`
  }
  if (mcNumber) {
    result += ` - (MC#: ${mcNumber})`
  }
  return result
}

/**
 * Format carrier name like SelectAsync.
 * Prefers DOT# (post P2-1245 carrier migration), falls back to MC# when
 * DOT# is absent (older dispatch snapshots). Pattern: "CarrierName - (DOT#: x)".
 */
export const formatCarrierName = (
  carrier?: {
    carrierDetails?: {
      carrierName?: string
      mcNumber?: string
      dotNumber?: string
    }
    branch?: {
      name?: string
    }
  } | null
): string => {
  if (!carrier) return ''
  const name = carrier.carrierDetails?.carrierName || carrier.branch?.name || ''
  if (!name) return ''

  const { dotNumber, mcNumber } = carrier.carrierDetails || {}
  if (dotNumber) return `${name} - (DOT#: ${dotNumber})`
  if (mcNumber) return `${name} - (MC#: ${mcNumber})`
  return name
}

/**
 * Carrier identifier line for cards — prefer DOT# (P2-1245), fall back to
 * MC# when DOT# is absent. Brokers/customers keep MC# (no DOT#) — see
 * formatCustomerName.
 */
export const formatCarrierIdentifier = (
  carrierDetails?: {
    mcNumber?: string | null
    dotNumber?: string | null
  } | null
): string => {
  if (!carrierDetails) return ''
  const { dotNumber, mcNumber } = carrierDetails
  if (dotNumber) return `DOT# ${dotNumber}`
  if (mcNumber) return `MC# ${mcNumber}`
  return ''
}

/**
 * Format commodity - convert snake_case to Title Case
 * Pattern from main app: formatCommodity in loadFieldFormatters.ts
 */
export const formatCommodity = (value?: string | null): string => {
  if (!value) return ''
  return value
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, char => char.toUpperCase())
}

/**
 * Format temperature with proper display
 * Pattern: "32°F to 38°F Continuous" or "As Per BOL"
 */
export const formatTemperature = (
  freightDetails?: {
    isAsPerBOL?: boolean
    temperature?: { min?: number; max?: number }
    temperatureType?: number // 1 = F, 2 = C
    cycle?: string // 'contn' = Continuous, 'cycle' = Cycle
  } | null
): string => {
  if (!freightDetails) return ''

  // Check if As Per BOL
  if (freightDetails.isAsPerBOL) {
    return 'As Per BOL'
  }

  const temp = freightDetails.temperature
  if (temp?.min == null && temp?.max == null) return ''

  // Temperature unit
  const unit = freightDetails.temperatureType === 2 ? '°C' : '°F'

  // Cycle mode
  const cycleMap: Record<string, string> = {
    contn: 'Continuous',
    cycle: 'Cycle'
  }
  const mode = freightDetails.cycle ? cycleMap[freightDetails.cycle] || '' : ''

  // Format temperature range
  let tempStr = ''
  if (temp?.min != null && temp?.max != null) {
    tempStr = `${temp.min}${unit} to ${temp.max}${unit}`
  } else if (temp?.min != null) {
    tempStr = `${temp.min}${unit}`
  } else if (temp?.max != null) {
    tempStr = `${temp.max}${unit}`
  }

  return mode ? `${tempStr} ${mode}` : tempStr
}

/**
 * Format rate type
 * Pattern: 1 = "Line Haul", 2 = "TONU"
 */
export const formatRateType = (type?: number | null): string => {
  const typeMap: Record<number, string> = {
    1: 'Line Haul',
    2: 'TONU'
  }
  return type ? typeMap[type] || '' : ''
}

/**
 * Format rate details summary
 * Pattern: "Line Haul - $2,920.00 - Description"
 */
export const formatRateDetailsSummary = (
  rateDetails?: {
    rate?: string
    type?: number
    description?: string
    additionalCharges?: {
      type?: string
      amount?: string
      description?: string
    }[]
  } | null
): { main: string; additional: string[] } => {
  if (!rateDetails) return { main: '-', additional: [] }

  const parts: string[] = []

  // Rate type
  const rateType = formatRateType(rateDetails.type)
  if (rateType) parts.push(rateType)

  // Rate amount
  if (rateDetails.rate) {
    const amount = formatCurrency(rateDetails.rate)
    // An unreadable rate is left out instead of showing a bare '-'
    if (amount !== '-') parts.push(amount)
  }

  // Description
  if (rateDetails.description) {
    parts.push(`"${rateDetails.description}"`)
  }

  // Additional charges
  const additional: string[] = []
  if (rateDetails.additionalCharges?.length) {
    rateDetails.additionalCharges.forEach((charge, idx) => {
      const chargeType = charge.type || `Charge ${idx + 1}`
      const amount = formatCurrency(charge.amount)
      const desc = charge.description ? `: "${charge.description}"` : ''
      additional.push(`${chargeType}: ${amount}${desc}`)
    })
  }

  return {
    main: parts.length > 0 ? parts.join(' - ') : '-',
    additional
  }
}

/**
 * Calculate total rate from rate details
 */
export const calculateTotalRate = (
  rateDetails?: {
    rate?: string
    additionalCharges?: { amount?: string }[]
  } | null
): number => {
  if (!rateDetails) return 0
  try {
    let total = parseAmount(rateDetails.rate || '0') || 0
    if (rateDetails.additionalCharges?.length) {
      rateDetails.additionalCharges.forEach(charge => {
        total += parseAmount(charge.amount || '0') || 0
      })
    }
    return total
  } catch {
    return 0
  }
}

/**
 * Format sales person details
 * Pattern: "Name (email)"
 */
export const formatSalesPerson = (
  salesPerson?: {
    name?: string
    email?: string
  } | null
): string => {
  if (!salesPerson?.name) return ''
  const email = salesPerson.email ? ` (${salesPerson.email})` : ''
  return `${salesPerson.name}${email}`
}

/**
 * Get driver full name from dispatch driver details.
 * Same text as the web's Dispatch tab driver column (selectAsyncLabels.ts
 * 'driverList' with formatName): "B18ID - First Middle Last", names as saved.
 */
export const getDriverName = (
  driverDetails?: {
    b18Id?: string
    firstName?: string
    middleName?: string
    lastName?: string
  } | null
): string => {
  if (!driverDetails) return ''
  const { b18Id, firstName, middleName, lastName } = driverDetails
  const name = [firstName, middleName, lastName].filter(Boolean).join(' ')
  if (!b18Id) return name
  return name ? `${b18Id} - ${name}` : b18Id
}

/**
 * Get driver phone from dispatch driver details.
 * Mobile number first, then the address phone, same order as the web app.
 */
export const getDriverPhone = (
  driverDetails?: {
    mobileNumber?: string
    addressDetails?: { telephone?: string }
  } | null
): string => {
  return (
    driverDetails?.mobileNumber ||
    driverDetails?.addressDetails?.telephone ||
    ''
  )
}

/**
 * Format MC number for display
 */
export const formatMcNumber = (mcNumber?: string | null): string => {
  if (!mcNumber) return ''
  return `MC# ${mcNumber}`
}

/**
 * Format facility name with address
 * Pattern: "FacilityName - Address, City, State, Zip"
 */
export const formatFacilityWithAddress = (
  facilityDetails?: {
    name?: string
    addressDetails?: {
      address?: string
      city?: string
      state?: string
      zipCode?: string
    }
  } | null,
  cityStateAddressDetails?: {
    city?: string
    state?: string
    formattedAddress?: string
  } | null
): { name: string; address: string } => {
  const name = facilityDetails?.name || ''
  let address = ''

  if (facilityDetails?.addressDetails) {
    const addr = facilityDetails.addressDetails
    const parts = [addr.address, addr.city, addr.state, addr.zipCode].filter(
      Boolean
    )
    address = parts.join(', ')
  } else if (cityStateAddressDetails) {
    if (cityStateAddressDetails.formattedAddress) {
      address = cityStateAddressDetails.formattedAddress
    } else {
      const parts = [
        cityStateAddressDetails.city,
        cityStateAddressDetails.state
      ].filter(Boolean)
      address = parts.join(', ')
    }
  }

  return { name, address }
}

/**
 * Format full address
 */
export const formatFullAddress = (
  addressDetails?: {
    address?: string
    city?: string
    state?: string
    zipCode?: string
    country?: string
  } | null
): string => {
  if (!addressDetails) return ''
  const parts = [
    addressDetails.address,
    addressDetails.city,
    addressDetails.state,
    addressDetails.zipCode
  ].filter(Boolean)
  return parts.join(', ')
}

/**
 * Format package summary for display
 * Pattern: "PU#: PKG001" or "DP#: PKG002"
 */
export const formatPackageId = (
  packageId?: string | null,
  eventType?: string
): string => {
  if (!packageId) return ''
  const cleanId = packageId.replace(/[^a-zA-Z0-9\s]/g, '')
  const prefix = eventType === 'drop' ? 'DP#' : 'PU#'
  return `${prefix}: ${cleanId}`
}
